'use client'

import React, { HTMLAttributes, ReactNode, useId } from 'react'
import { TabList } from '../../molecules/Tabs'
import { TabbedPanelProvider } from '../../molecules/Tabs/tab-context'

interface TabbedPanelProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode
    tabContent: ReactNode
    activeTabId: string
}

/**
 * @description Props for the TabbedPanel component when there is not a visible label for the tab list
 */
export interface TabbedPanelLabelRequiredProps extends TabbedPanelProps {
    /**@description The screen reader label for the tab list */
    tabListLabel: string
    /**@description The id of the element that labels the tab list */
    tabListLabelledBy?: string
}

/**
 * @description Props for the TabbedPanel component when there is a visible label for the tab list
 */
export interface TabbedPanelLabelledByRequiredProps extends TabbedPanelProps {
    /**@description The screen reader label for the tab list */
    tabListLabel?: string
    /**@description The id of the element that labels the tab list */
    tabListLabelledBy: string
}

export const TabbedPanel = ({
    children,
    tabContent,
    activeTabId,
    tabListLabel,
    tabListLabelledBy,
    ...props
}: TabbedPanelLabelledByRequiredProps | TabbedPanelLabelRequiredProps) => {
    const panelId = `${useId()}-panel`

    return (
        <TabbedPanelProvider value={{ panelId }}>
            <div {...props} className={`tabs-wrapper flex flex-col min-h-full ${props.className ?? ''}`}>
                <TabList ariaLabel={tabListLabel} ariaLabelledBy={tabListLabelledBy}>
                    {children}
                </TabList>
                <div role="tabpanel" id={panelId} aria-labelledby={activeTabId} className="flex flex-col grow">
                    {tabContent}
                </div>
            </div>
        </TabbedPanelProvider>
    )
}
